import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import routes from './routes';
import useJumboRoutes from '@jumbo/hooks/useJumboRoutes';
import { logoutUser } from './redux/slice/authSlice';

const SESSION_EXPIRED_MSG = 'Access not allowed at this time. You have been logged out.';
const USER_INACTIVE_MSG = 'Your account has been deactivated. Please contact the master admin.';

const AppRoutes = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const common = useSelector((state) => state?.common);
  const authUser = useSelector((state) => state?.auth?.loggedUser);
  const settings = useSelector((state) => state?.settings);
  const logoutInProgress = useRef(false);

  const doLogout = (userId) => {
    if (logoutInProgress.current) return;
    logoutInProgress.current = true;
    dispatch(logoutUser(userId))
      .unwrap()
      .then((res) => {
        if (res?.status === 200) {
          localStorage.clear();
          navigate('/login');
        }
      })
      .finally(() => {
        logoutInProgress.current = false;
      });
  };

  // Logout on backend-driven session expiry messages
  useEffect(() => {
    const expired =
      common?.errorMessage === 'Token Expired.' ||
      common?.errorMessage === 'Token not passed.' ||
      common?.errorMessage === 'Invalid token.' ||
      common?.errorMessage === 'Your token has expired!' ||
      common?.errorMessage === SESSION_EXPIRED_MSG ||
      common?.errorMessage === USER_INACTIVE_MSG;

    if (expired) {
      doLogout(authUser?._id);
    }
  }, [common, dispatch, navigate, authUser?._id]);

  // Proactive frontend timer: check every 60s if current time is still within allowed hours
  useEffect(() => {
    if (!authUser) return;

    const storedUser = JSON.parse(localStorage.getItem('User') || '{}');
    const isMasterAdmin = storedUser?.roleId?.slug === 'master-admin';
    const alwaysAllow = storedUser?.alwaysAllow;

    if (isMasterAdmin || alwaysAllow) return;

    const check = () => {
      const hour = new Date().getHours();
      const start = settings?.loginStartHour ?? 10;
      const end = settings?.loginEndHour ?? 19;
      if (hour < start || hour >= end) {
        doLogout(authUser?._id);
      }
    };

    check(); // run immediately on mount
    const timer = setInterval(check, 60000); // then every minute
    return () => clearInterval(timer);
  }, [authUser, settings?.loginStartHour, settings?.loginEndHour]);

  const appRoutes = useJumboRoutes(routes);
  return <React.Fragment>{appRoutes}</React.Fragment>;
};
export default AppRoutes;
